import { useCallback, useEffect } from 'react'

/**
 * Hook for keyboard navigation between tabs.
 * 
 * Shortcuts:
 * - Ctrl/Cmd + PgUp: Previous tab
 * - Ctrl/Cmd + PgDn: Next tab
 * 
 * @example
 * ```tsx
 * useTabKeyboardNav({
 *   tabs: TAB_ORDER,
 *   currentIndex,
 *   onTabChange: (tab) => setSelectedTab(tab),
 * })
 * ```
 */
export interface UseTabKeyboardNavConfig<TTab extends string> {
	/** Current tab index */
	currentIndex: number,
	/** Whether keyboard navigation is enabled (default: true) */
	enabled?: boolean,
	/** Tab change handler */
	onTabChange: (tab: TTab) => void
	/** Ordered array of tab IDs */
	tabs: readonly TTab[]
}

export const useTabKeyboardNav = <TTab extends string>({
	tabs,
	currentIndex,
	onTabChange,
	enabled = true,
}: UseTabKeyboardNavConfig<TTab>) => {
	const goToPrevTab = useCallback(() => {
		if (tabs.length === 0) return
		const prevIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1
		onTabChange(tabs[prevIndex])
	}, [tabs, currentIndex, onTabChange])

	const goToNextTab = useCallback(() => {
		if (tabs.length === 0) return
		const nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0
		onTabChange(tabs[nextIndex])
	}, [tabs, currentIndex, onTabChange])

	useEffect(() => {
		if (!enabled) return

		const handleKeyDown = (event: KeyboardEvent) => {
			const { key, ctrlKey, metaKey } = event
			const modKey = ctrlKey || metaKey

			if (!modKey) return

			// Ctrl/Cmd + PageUp: Previous tab
			if (key === 'PageUp') {
				event.preventDefault()
				event.stopPropagation()
				goToPrevTab()
			}

			// Ctrl/Cmd + PageDown: Next tab
			if (key === 'PageDown') {
				event.preventDefault()
				event.stopPropagation()
				goToNextTab()
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [enabled, goToPrevTab, goToNextTab])

	return {
		goToPrevTab,
		goToNextTab,
	}
}

